import { FSMNamedState, ObservableFiniteStateMachine, OMQuery, OMReference } from "@apptivity-lab/firmament-node-sdk"
import { AffExpertProfile } from "@firmament-packages/aff"
import AffRating from "@firmament-packages/aff/AffRating"
import { CnxMeetingTemplate } from "@firmament-packages/connectx"

declare namespace AffRatingListStateMachine {
    export interface Start extends FSMNamedState {
        name: "start"
    }

    export interface Loading extends FSMNamedState {
        name: "loading"
        ratings: AffRating[]
        query: OMQuery<AffRating>
    }

    export interface Presenting extends FSMNamedState {
        name: "presenting"
        ratings: AffRating[]
        query: OMQuery<AffRating>
    }

    export interface ShowingError extends FSMNamedState {
        name: "showingError"
        error: Error
        ratings: AffRating[]
        query: OMQuery<AffRating>
    }

    export type State = Start |
        Loading |
        Presenting |
        ShowingError
}

export type AffRatingQueryConfiguration = {
    meetingTemplateId?: OMReference<CnxMeetingTemplate>,
    expertProfileId?: OMReference<AffExpertProfile>,
    limit?: number
}

export class AffRatingListStateMachine extends ObservableFiniteStateMachine<AffRatingListStateMachine.State> {
    private queryConfig?: AffRatingQueryConfiguration

    constructor() {
        super({ name: "start" })
    }

    async refresh(queryConfig: AffRatingQueryConfiguration) {
        let existingRatings: AffRating[]

        // Keep track of query config
        this.queryConfig = queryConfig

        switch (this.state.name) {
            case "start":
                existingRatings = []
                break
            case "presenting":
                existingRatings = this.state.ratings
                break
            default:
                return
        }

        try {
            let query = new OMQuery(AffRating)
                .include("submitter")
                .include("submitter.photo" as any)
                .sort("createdAt", "DESC")
                .limit(queryConfig.limit || 20)

            if (queryConfig.meetingTemplateId) {
                query.filter("meetingTemplate", "equals", queryConfig.meetingTemplateId.id as any)
            }
            if (queryConfig.expertProfileId) {
                query.filter("provider", "equals", queryConfig.expertProfileId.id as any)
            }

            this.state = {
                name: "loading",
                ratings: existingRatings,
                query
            }

            await query.execute()

            this.success(query, query.resultObjects)
        } catch (error: unknown) {
            if (error instanceof Error) {
                this.fail(error)
            }
        }
    }

    private success(query: OMQuery<AffRating>, ratings: AffRating[]) {
        switch (this.state.name) {
            case "loading":
                this.state = { name: "presenting", ratings, query }
                break
            default:
                return
        }
    }

    private fail(error: Error) {
        switch (this.state.name) {
            case "loading":
                this.state = { name: "showingError", error, ratings: this.state.ratings, query: this.state.query }
                break
            default:
                return
        }
    }
}
